/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import generateFullDocx from "../../utils/docxGenerator";

import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Chip, Stack, Button, CircularProgress } from "@mui/material";

import IntroductionStartCard from "./IntroductionStartCard";
import IntroductionPagination from "./IntroductionPagination";
import IntroductionDialog from "./IntroductionDialog";
import ExperimentForm from "../ExperimentForm";
import Timeline from "../Timeline";
import DocPreview from "../DocPreview";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function Introduction({
  showConfirmDialog,
  onConfirmDialogClose,
  forceReset,
  onForceResetHandled,
  onSaveProject,
  projectToEdit
}) {
  const [step, setStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [pageCount, setPageCount] = useState("");
  const [formData, setFormData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [duration, setDuration] = useState({});
  const [showHomeDialog, setShowHomeDialog] = useState(false);
  const timelineRefs = React.useRef([]);
  const [capturing, setCapturing] = useState(false);
  const [capturedImages, setCapturedImages] = useState([]);
  const [captureReady, setCaptureReady] = useState(false);
  const [currentLocalStep, setCurrentLocalStep] = useState(0);

  useEffect(() => {
    if (projectToEdit) {
      setProjectName(projectToEdit.name);
      setFormData(projectToEdit.data || []);
      setStep(1);
    }
  }, [projectToEdit]);

  console.log("Form Data JSON :", JSON.stringify(formData, null, 2));


  useEffect(() => {
    if (showConfirmDialog && step > 0) {
      setOpenDialog(true);
    } else {
      onConfirmDialogClose(false);
    }
  }, [showConfirmDialog, step, finished, onConfirmDialogClose]);

  useEffect(() => {
    if (forceReset) {
      resetForm();
      onForceResetHandled();
    }
  }, [forceReset, onForceResetHandled]);

  const handleStart = useCallback(() => {
    const count = parseInt(pageCount);
    if (!isNaN(count) && count > 0) {
      const initialData = Array.from({ length: count }, (_, idx) => ({
        objectiveLabel: `Objective ${idx + 1}`,
        experimentObjective: `Experiment ${idx + 1}`,
        interventions: [{ lastIntervention: "yes" }],
      }));
      setFormData(initialData);
      setStep(1);
    }
  }, [pageCount]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === "Enter" &&
        step === 0 &&
        pageCount.trim() !== "" &&
        !isNaN(pageCount) &&
        parseInt(pageCount) > 0 &&
        projectName.trim() !== ""
      ) {
        handleStart();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [step, pageCount, projectName, handleStart]);

  const resetForm = () => {
    setStep(0);
    setFinished(false);
    setPageCount("");
    setFormData([]);
    setProjectName("");
  };

  const handleJsonImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);

          // Extract projectName and formData from the JSON
          const loadedProjectName = jsonData.projectName || "";
          const loadedFormData = Array.isArray(jsonData.formData) ? jsonData.formData : [];

          // Corriger lastIntervention
          loadedFormData.forEach((exp) => {
            exp.interventions?.forEach((intv, idx, arr) => {
              intv.lastIntervention = idx === arr.length - 1 ? "yes" : "no";
            });
          });

          // Update state with loaded data
          setProjectName(loadedProjectName);
          setFormData(loadedFormData);
          setStep(loadedFormData.length > 0 ? 1 : 0);
        } catch (parseErr) {
          console.error("Erreur lors du parsing du JSON :", parseErr);
          alert("Erreur : format JSON invalide.");
        }
      };
      reader.readAsText(file);
    } catch (err) {
      console.error("Erreur lors de l'importation du JSON :", err);
      alert("Erreur : impossible de lire ce fichier JSON.");
    }
  };
  
  const hasEmptyRequiredFields = () => {
    let hasError = false;
    const invalidFormIndexes = [];

    formData.forEach((entry, formIndex) => {
      let formHasError = false;
      entry.interventions.forEach((intervention) => {
        const type = intervention.interventionType || "";
        const method = intervention.interventionMethod || "";

        const isTypeOther = type.toLowerCase().includes("other");
        const isMethodOther = method.toLowerCase().includes("other");

        const hasType = type !== "";
        const hasTypeOther = !isTypeOther || (intervention.interventionTypeOther && intervention.interventionTypeOther.trim() !== "");
        
        const hasMethod = isTypeOther
          ? true
          : method !== "" && (!isMethodOther || (intervention.interventionMethodOther && intervention.interventionMethodOther.trim() !== ""));

        let interventionHasError = false;
        if (hasType) {
          interventionHasError = !hasTypeOther || !hasMethod;
        }
        intervention._fieldError = interventionHasError;

        if (interventionHasError) {
          formHasError = true;
        }
      });

      if (formHasError) {
        hasError = true;
        invalidFormIndexes.push(formIndex + 1);
      }
    });
    setFormData([...formData]);
    if (hasError) {
      alert(`Please fill all required field in page(s): ${invalidFormIndexes.join(", ")}`);
    }
    return hasError;
  };

  const submit = () => {
    if (hasEmptyRequiredFields()) {
      return;
    }
    const localDuration = {};
    const invalidMessages = [];

    formData.forEach((entry, index) => {
      const declared = parseInt(entry.experimentDuration || "0", 10);
      let maxDay = 0;
      const acclimatation = parseInt(entry.acclimatationDays || 0, 10);
      if (acclimatation > 0) maxDay = Math.max(maxDay, acclimatation + 1);

      const preparation = parseInt(entry.preparationDays || 0, 10);
      const acclimation = parseInt(entry.acclimatationDays || 0, 10);
      if (preparation > 0 && entry.preparation !== "no_preparation") {
        const prepStart = acclimation > 0 ? acclimation + 1 : 1;
        const prepEnd = prepStart + preparation - 1;
        maxDay = Math.max(maxDay, prepEnd + 1);
      }

      entry.interventions.forEach((intervention) => {
        const start = parseInt(intervention.daysStart || 0, 10);

        if (intervention.repeatedIntervention === "specific_days" && intervention.specificDays) {
          intervention.specificDays.split(",").forEach((d) => {
            const day = parseInt(d.trim(), 10);
            if (!isNaN(day)) maxDay = Math.max(maxDay, day);
          });
        } else if (intervention.repeatedIntervention === "every_day" && intervention.xDays) {
          for (let i = 0; i < parseInt(intervention.xDays, 10); i++) {
            maxDay = Math.max(maxDay, start + i);
          }
        } else if (
          intervention.repeatedIntervention === "every_x_days" &&
          intervention.xDays &&
          intervention.yTimes
        ) {
          for (let i = 0; i < parseInt(intervention.yTimes, 10); i++) {
            maxDay = Math.max(maxDay, start + i * parseInt(intervention.xDays, 10));
          }
        } else if (intervention.repeatedIntervention === "after_x_days" && intervention.xDays) {
          maxDay = Math.max(maxDay, start + parseInt(intervention.xDays, 10));
        } else {
          maxDay = Math.max(maxDay, start);
        }
      });
      
      localDuration[index] = maxDay;

      if (maxDay > declared) {
        invalidMessages.push(
          `In page ${index+1}, the declared experience duration is ${declared} days, but the actual duration is ${maxDay} days.\n`
        );
        entry._formError = true;
      } else {
        entry._formError = false;
      }
    });

    setDuration(localDuration);
    
    if (invalidMessages.length > 0){
      alert("Error\n"+invalidMessages.join("\n")+"\nPlease correct first question on all the forms that is causing problems.");
      return;
    }

    onSaveProject(projectName, formData);
    setFinished(true);
  };

  // Auto-capture timelines when the results page loads
  useEffect(() => {
    if (!finished) return;
    setCaptureReady(false);
    setCapturedImages([]);
    const timer = setTimeout(async () => {
      const images = [];
      for (let i = 0; i < formData.length; i++) {
        const ref = timelineRefs.current[i];
        if (ref && ref.capture) {
          try {
            const result = await ref.capture();
            images.push(result || null);
          } catch (e) {
            images.push(null);
          }
        } else {
          images.push(null);
        }
      }
      setCapturedImages(images);
      setCaptureReady(true);
    }, 1200); // allow vis-timeline to fully render
    return () => clearTimeout(timer);
  }, [finished]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateDocx = async () => {
    setCapturing(true);
    await new Promise((resolve) => setTimeout(resolve, 100)); // let sticky bar hide before capture
    setCapturing(false);
    generateFullDocx(formData, projectName, capturedImages);
  };

  const handleDownloadJson = () => {
    const jsonData = {
      projectName: projectName,
      formData: formData
    };

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName}_form_data.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleConfirm = (confirm) => {
    if (confirm) {
      resetForm();
    }
    setOpenDialog(false);
    onConfirmDialogClose(confirm);
  };


  if (step === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <IntroductionStartCard
          pageCount={pageCount}
          setPageCount={setPageCount}
          handleStart={handleStart}
          projectName={projectName}
          setProjectName={setProjectName}
          handleJsonImport={handleJsonImport}
        />
      </Box>
    );
  }

  if (finished) {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#f0f2f5" }}>

        {/* Off-screen timelines rendered for image capture only */}
        <Box sx={{ position: "absolute", left: "-9999px", top: 0, width: "1400px", pointerEvents: "none" }}>
          {formData.map((expData, i) => (
            <Box key={i} sx={{ mb: 4 }}>
              <Timeline
                ref={(el) => (timelineRefs.current[i] = el)}
                data={[expData]}
                index={i}
                projectName={projectName}
                duration={duration[i]}
                title={`Timeline for Experiment ${i + 1}`}
              />
            </Box>
          ))}
        </Box>

        {/* Sticky top bar */}
        <Box sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          visibility: capturing ? "hidden" : "visible",
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
        }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mx: "auto" }}>
            Document Preview
          </Typography>
        </Box>

        {/* Loading state */}
        {!captureReady && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 6, gap: 2 }}>
            <CircularProgress size={32} />
            <Typography color="text.secondary" fontSize="0.9rem">
              Generating preview…
            </Typography>
          </Box>
        )}

        {/* Document preview */}
        {captureReady && (
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: { xs: 2, sm: 4 },
            py: 3,
          }}>
            {/* A4 Preview Container */}
            <Box
              sx={{
                width: "210mm", // A4 width
                maxHeight: "calc(100vh - 250px)",
                overflow: "auto",
                backgroundColor: "white",
                boxShadow: "0 8px 32px rgba(30, 58, 138, 0.12)",
                borderRadius: 3,
                padding: 4,
                mb: 4,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                border: "1px solid rgba(30, 58, 138, 0.08)",
                "&:hover": {
                  boxShadow: "0 12px 48px rgba(30, 58, 138, 0.18)",
                  transform: "translateY(-4px)"
                },
                // Custom scrollbar
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f5f9",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "10px",
                  "&:hover": {
                    backgroundColor: "#94a3b8",
                  },
                },
              }}
            >
              <DocPreview
                formData={formData}
                projectName={projectName}
                timelineImages={capturedImages}
              />
            </Box>

            {/* Action buttons below */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                justifyContent: "center",
                maxWidth: "800px",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleGenerateDocx}
                sx={{
                  fontWeight: 600,
                  boxShadow: "0 4px 14px rgba(30, 58, 138, 0.25)",
                }}
              >
                Download .docx
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="medium"
                onClick={handleDownloadJson}
                sx={{
                  fontWeight: 600,
                  boxShadow: "0 4px 14px rgba(14, 165, 233, 0.25)",
                }}
              >
                Download JSON
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                startIcon={<ArrowBackIcon />}
                onClick={() => { setFinished(false); setCapturedImages([]); setCaptureReady(false); }}
                sx={{ fontWeight: 600 }}
              >
                Back to Form
              </Button>
            </Box>
          </Box>
        )}

        <IntroductionDialog open={openDialog} handleConfirm={handleConfirm} finished={true} />
      </Box>
    );
  }

  return (
    <Box className="container">
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, px: 2, pt: 5 }}>
        <label htmlFor="import-json">
          <input
            type="file"
            id="import-json"
            accept=".json"
            style={{ display: "none" }}
            onChange={handleJsonImport}
          />
          <Chip
            label="Import JSON"
            clickable
            component="span"
            color="secondary"
            variant="contained"
          />
        </label>
      </Box>
      <header>
        <Box mt={3}>
          <Typography variant="h5" mb={2} textAlign="center">
            Experiment {step}
          </Typography>
          <ExperimentForm
            index={step - 1}
            data={formData}
            setData={setFormData}
            onLocalStepChange={setCurrentLocalStep}
          />
        </Box>
        <IntroductionPagination
          step={step}
          setStep={setStep}
          total={formData.length}
          submit={submit}
          localStep={currentLocalStep}
        />

        <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
          <Chip
            label="Add Experiment"
            icon={<AddCircleOutlineIcon />}
            variant="outlined"
            onClick={() => {
              const newFormData = [...formData];
              const newExperimentNumber = newFormData.length + 1;
              newFormData.splice(step, 0, {
                objectiveLabel: `Objective ${newExperimentNumber}`,
                experimentObjective: `Experiment ${newExperimentNumber}`,
                interventions: [{ lastIntervention: "yes" }],
              });
              setFormData(newFormData);
            }}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": { backgroundColor: "primary.light", color: "white" },
            }}
          />

          <Chip
            label="Delete Experiment"
            icon={<DeleteOutlineIcon />}
            variant="outlined"
            disabled={formData.length <= 1}
            onClick={() => {
              const confirmed = window.confirm(
                "Are you sure you want to delete this experiment? Unsaved changes will be lost."
              )
              if (!confirmed) return;
              if (formData.length > 1) {
                const newFormData = [...formData];
                newFormData.splice(step - 1, 1);
                setFormData(newFormData);
                setStep((prev) => Math.max(1, prev - 1));
              }
            }}
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              "&:hover": {
                backgroundColor: formData.length > 1 ? "error.light" : "inherit",
                color: formData.length > 1 ? "white" : "inherit",
              },
            }}
          />
        </Stack>
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => setShowHomeDialog(true)}
            sx={{
              backgroundColor: 'primary.dark',
              color: "#fff",
              '&:hover': {
                backgroundColor: '#fff',
                color: 'primary.dark'
              }
            }}
          >
            Back to Home
          </Button>
        </Box>
        <IntroductionDialog
          open={showHomeDialog}
          handleConfirm={(confirm) => {
            if (confirm) resetForm();
            setShowHomeDialog(false);
          }}
          finished={false}
        />
      </header>

      <IntroductionDialog open={openDialog} handleConfirm={handleConfirm} finished={false} />
    </Box>
  );
}
